import { useState, useMemo, useRef, useEffect } from 'react';
import { formatDateToLocal } from '@safetyhub/shared';
import { Button, Modal, TextButton } from '@safetyhub/ui';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from '../../../../components/modalLayers';
import { getUserIdentifier, isSiteSelectable, OrganizationMember, organizationsApi, OWNER_LABEL } from '../../../../lib/organizations';
import { apiErrorMessage } from '../../../../lib/api';
import { isOrganizationMember } from '../../../../lib/members';
import { RoleBadge } from '../../../../components/RoleBadge';
import { MembersModals, MembersHandlers, MembersData } from '../types';
import { BuildingIcon, ExclamationIcon } from './Icons';
import { XIcon } from '../../../../components/icons';
import { PersonAvatar } from '../../../../components/PersonAvatar';
import { DirtyHint } from '../../../../components/common/DirtyHint';
import { useAuthStore } from '../../../../stores/auth';

/** 멤버 이름 우선 표시: member.name > user.name > '-' */
function getMemberDisplayName(target: OrganizationMember): string {
  return target.member?.name || target.user?.name || '-';
}

interface MemberDetailModalProps {
  modals: MembersModals;
  handlers: MembersHandlers;
  data: MembersData;
  isMobile?: boolean;
}

export function MemberDetailModal({ modals, handlers, data, isMobile }: MemberDetailModalProps) {
  const { detailTarget, allSites, isLoadingSites, isAssigning, isUnassigning, assignConfirmSite, unassignConfirmSiteId, transferRequired, transferTargetMemberId, isTransferring } = modals;
  const { fetchUser } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // 정보 편집 (rank만 편집 가능, department/position은 SiteContact 참조)
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editRank, setEditRank] = useState('');
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [infoError, setInfoError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownListRef = useRef<HTMLUListElement>(null);

  const startEditInfo = () => {
    setEditRank(detailTarget?.rank || '');
    setIsEditingInfo(true);
  };

  // 편집 칸이 직급 한 필드뿐이라 기준점(저장된 값)이 늘 손에 있다 — 원본과 직접 비교한다.
  // 비교는 저장이 보내는 형태(trim)로.
  const isInfoDirty = editRank.trim() !== (detailTarget?.rank ?? '');

  const handleSaveInfo = async () => {
    if (!detailTarget) return;
    if (!isInfoDirty) return;
    setIsSavingInfo(true);
    setInfoError('');
    try {
      await organizationsApi.updateMemberInfo(
        detailTarget.companyId,
        detailTarget.id,
        { rank: editRank.trim() }
      );
      await handlers.refreshMembers();
      // 멤버 정보 수정 후 auth store도 갱신 (본인일 경우 Profile 즉시 반영, 다른 멤버일 경우 조직 정보 동기화)
      await fetchUser();
      setIsEditingInfo(false);
    } catch (err) {
      // axios 원문(「Network Error」 등)이 새지 않게 서버 문구만 쓰고 나머지는 일반 문구로(리뷰 1103 F7)
      setInfoError(apiErrorMessage(err) ?? '멤버 정보를 저장하지 못했습니다.');
    } finally {
      setIsSavingInfo(false);
    }
  };

  // 드롭다운 외부 클릭 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 현장 추가 목록은 절대배치(`absolute`)라 base 본문 스크롤 영역(`overflow-y-auto`) 경계에
  // 잘린다 — 그 콤보박스가 본문 맨 아래에 있어 목록이 대부분 경계 밖으로 나간다. 열릴 때 본문을
  // 그 목록이 보이는 위치까지 스크롤한다(본문은 길이 상한이 없어 base 본문 스크롤을 끌 수 없다 —
  // 등록된 역할 목록이 상한 없이 늘어난다). jsdom에는 `scrollIntoView`가 없어 선택적 호출이다.
  useEffect(() => {
    if (isDropdownOpen) dropdownListRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [isDropdownOpen]);

  // 모달 닫힐 때 검색어/편집 초기화
  useEffect(() => {
    if (!detailTarget) {
      setSearchQuery('');
      setIsDropdownOpen(false);
      setIsEditingInfo(false);
      setInfoError('');
      setEditRank('');
    }
  }, [detailTarget]);

  // 탈퇴일
  const deletedAt = detailTarget?.user?.deletedAt
    ? formatDateToLocal(new Date(detailTarget.user.deletedAt))
    : null;

  // 이미 할당된 현장 ID Set
  const assignedSiteIds = useMemo(() => {
    if (!detailTarget) return new Set<string>();
    return new Set(detailTarget.siteAssignments?.map(a => a.siteId) || []);
  }, [detailTarget]);

  // 검색 필터된 미할당 현장 목록
  const filteredSites = useMemo(() => {
    const unassigned = allSites.filter(s => !assignedSiteIds.has(s.id) && isSiteSelectable(s));
    if (!searchQuery.trim()) return unassigned;
    const q = searchQuery.trim().toLowerCase();
    return unassigned.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.address?.toLowerCase().includes(q)
    );
  }, [allSites, assignedSiteIds, searchQuery]);

  if (!detailTarget) return null;

  const assignedSites = detailTarget.siteAssignments?.map(a => {
    const site = allSites.find(s => s.id === a.siteId);
    // 이름은 할당 payload가 이미 들고 있다(`siteAssignments[].site`) — allSites는 주소용 보강일 뿐이라
    // 로딩 전에도 이름이 비지 않는다. 폴백을 siteId로 두면 로딩 중 raw CUID가 화면에 노출된다.
    return { ...a, siteName: a.site?.name || site?.name || '이름 없음', siteAddress: site?.address };
  }) || [];

  // 해제 확인 대상 현장명
  const unassignSiteName = unassignConfirmSiteId
    ? (allSites.find(s => s.id === unassignConfirmSiteId)?.name || '')
    : '';

  // 하단 버튼줄의 두 축(아래 JSX 주석이 정본). 종전에는 외곽 조건 하나가 둘을 함께 들고
  // `!detailTarget.isOwner`를 품어, **소유자 대상에게는 버튼줄이 통째로 안 떴다** — 그래서
  // 소유자 해제가 web에서 도달 불가였다(서버·확인 모달·핸들러는 이미 양방향).
  const isSelf = detailTarget.userId === data.currentUserId;
  /**
   * 소유자 축(지정·해제) — 서버 `updateMemberRole`의 소유자 축 게이트와 **같은 술어**다:
   * 컨트롤러 `@Permissions('member.manage')` + 서비스가 요청자의 `isOwner`를 또 요구하고
   * (`ORGANIZATION_MEMBER_OWNER_CHANGE_FORBIDDEN`) 그 게이트는 **양방향**이다 — 「해제만 열어 두면
   * 소유권을 빼는 것은 아무나가 된다」(`organizations.service.ts`의 강등 분기 주석). 본인 제외는
   * 서버 `CANNOT_CHANGE_OWN_ROLE`의 거울상.
   *
   * ⚠️ **마지막 소유자는 여기서 숨기지 않는다** — 소유자 수를 web에서 재파생하면 서버
   * `listOwnerMemberIds`와 드리프트하고, 그 벽은 「다른 멤버를 소유자로 지정하면」 **풀리는**
   * 종류라 숨기면 이유가 안 보인다. 서버 `LAST_OWNER_CANNOT_CHANGE`가 그 안내 문구를 들고 있고
   * `useMembersState`의 `handleRoleChange`가 그것을 토스트로 띄운다(= 거부를 받아 줄 자리가 있다).
   */
  const canChangeOwnerAxis = data.canManageMembers && data.isOwner && !isSelf;
  /**
   * 제거 — 권한 **key** `member.manage`(`DELETE …/:memberId`). 소유자 대상에게 숨는 것은 **web 쪽
   * 좁히기**이고 서버 `removeMember`는 소유자 제거를 실제로 처리한다(`context: 'owner removal'`).
   * ⛔ 그래서 이 술어에서 `!detailTarget.isOwner`를 빼면 **그 경로가 조용히 열린다** — 종전 식을
   * 글자 그대로 보존한 것이 의도다(소유자 축을 갈라 넓힌 라운드가 이 축은 건드리지 않았다).
   */
  const canRemoveMember = data.canManageMembers && !isSelf && !detailTarget.isOwner;

  return (
    <>
    <Modal
      open
      onClose={() => handlers.setDetailTarget(null)}
      ariaLabel={getMemberDisplayName(detailTarget)}
      size={isMobile ? 'sm' : 'lg'}
      // 헤더는 아바타·배지·닫기를 든 **자체 헤더**(본문 첫 블록)가 정본이라 base 헤더를 그리지 않는다.
      showCloseButton={false}
      // 직급 저장 중에는 닫는 길을 막는다(종전 오버레이는 저장 중에도 닫았다).
      closeOnOverlay={!isSavingInfo}
      closeOnEsc={!isSavingInfo}
      closeLocked={isSavingInfo}
      footer={
        <>
          {/* 두 버튼의 축이 **다르다**(P7-g) — 술어는 위 `canChangeOwnerAxis`·`canRemoveMember`가 정본:
              - 「소유자 지정/해제」는 소유자 **성질** — 같은 축의 형제 경로가 이미 그렇게 좁혀졌다(초대의
                `ORGANIZATION_INVITE_OWNER_FORBIDDEN`·배정의 `ROLE_ASSIGNMENT_OWNER_FORBIDDEN`:
                「소유자 지정은 조직 소유자만」). `PATCH members/:memberId/role`의 컨트롤러 가드는
                `member.manage`지만 서비스가 요청자 `isOwner`를 **양방향으로** 또 요구하므로
                (`ORGANIZATION_MEMBER_OWNER_CHANGE_FORBIDDEN`) 화면의 `data.isOwner`가 서버와 일치한다.
              - 「제거」는 권한 **key** `member.manage`(`DELETE …/:memberId`) — 소유자 대상에게는 숨는다. */}
          {(canChangeOwnerAxis || canRemoveMember) && (
            <div className="mr-auto flex items-center gap-2">
              {/* 소유자 지정/해제(옛 「역할 변경」). 확인 모달·핸들러는 처음부터 양방향이었고
                  (`Modals.tsx`의 `newIsOwner ? '지정' : '해제'`) **이 트리거만 없어 해제가 도달
                  불가**였다 — 스토리 L19, 정본 ⓕ(`docs/decisions/2026-10-23-legal-document-obligation-management.md`).
                  대상이 소유자면 해제, 아니면 지정 — 한 버튼이 축의 두 방향을 든다. */}
              {canChangeOwnerAxis && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.setRoleChangeTarget(detailTarget);
                  handlers.setNewIsOwner(!detailTarget.isOwner);
                  handlers.setDetailTarget(null);
                }}
                className={`px-3 py-1.5 font-medium bg-white border border-line rounded-card-sm ${detailTarget.isOwner ? 'text-ink-600 hover:bg-ink-50' : 'text-brand-600 hover:bg-brand-50'} ${isMobile ? 'text-xs' : 'text-sm'}`}
              >
                {OWNER_LABEL} {detailTarget.isOwner ? '해제' : '지정'}
              </button>
              )}
              {canRemoveMember && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.setRemoveTarget(detailTarget);
                  handlers.setDetailTarget(null);
                }}
                className={`px-3 py-1.5 font-medium text-red-600 bg-white border border-line rounded-card-sm hover:bg-red-50 ${isMobile ? 'text-xs' : 'text-sm'}`}
              >
                제거
              </button>
              )}
            </div>
          )}
          <Button
            type="button"
            size={isMobile ? 'sm' : 'md'}
            onClick={() => handlers.setDetailTarget(null)}
            disabled={isSavingInfo}
          >
            닫기
          </Button>
        </>
      }
    >
          {/* 헤더: 멤버 정보 */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center">
              {/* 목업 `.mm-avatar` — 목록 행과 **같은 팔레트**여야 행에서 모달로 넘어와도 같은 사람으로 읽힌다 */}
              <PersonAvatar
                name={getMemberDisplayName(detailTarget)}
                seed={detailTarget.id}
                size={isMobile ? 'lg' : 'xl'}
              />
              <div className="ml-3">
                <div className="flex items-center gap-2">
                  <h3 className={`font-semibold text-ink-900 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    {getMemberDisplayName(detailTarget)}
                  </h3>
                  <RoleBadge member={detailTarget} />
                </div>
                <p className={`text-ink-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {detailTarget.user ? getUserIdentifier(detailTarget.user.auths) : '-'}
                </p>
                <p className={`text-ink-400 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                  가입일: {formatDateToLocal(new Date(detailTarget.createdAt))}
                </p>
                {deletedAt && (
                  <p className={`text-red-400 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                    탈퇴일: {deletedAt}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="닫기"
              onClick={() => handlers.setDetailTarget(null)}
              disabled={isSavingInfo}
            >
              <XIcon className="h-5 w-5" />
            </Button>
          </div>

          {/* 기본 정보 섹션 */}
          <div className="border-t border-line-soft pt-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-medium text-ink-900 ${isMobile ? 'text-sm' : 'text-base'}`}>기본 정보</h4>
              {/* 서버 `PATCH members/:memberId/info` = member.manage */}
              {data.canManageMembers && !isEditingInfo && (
                <TextButton onClick={startEditInfo} className="text-xs">
                  수정
                </TextButton>
              )}
            </div>

            {isEditingInfo ? (
              <div className="space-y-2">
                {infoError && (
                  <div className="rounded-card-sm bg-red-50 p-2">
                    <p className="text-xs text-red-700">{infoError}</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-ink-500 mb-1">직급</label>
                  <input
                    type="text"
                    value={editRank}
                    onChange={(e) => setEditRank(e.target.value)}
                    placeholder="예: 대리, 과장"
                    className="w-full px-3 py-2 border border-line rounded-control text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveInfo}
                    disabled={isSavingInfo || !isInfoDirty}
                  >
                    {isSavingInfo ? '저장 중...' : '저장'}
                  </Button>
                  <Button size="sm" onClick={() => setIsEditingInfo(false)}>
                    취소
                  </Button>
                  {/* 저장이 왜 비활성인지 말해준다(수정 모달 dirty 표준의 "이유 표시"). */}
                  <DirtyHint isDirty={isInfoDirty} className="text-xs" />
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <dt className="text-xs text-ink-500">직급</dt>
                  <dd className="text-ink-900">{detailTarget.rank || '-'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-500">연락처</dt>
                  <dd className="text-ink-900">
                    {detailTarget.user?.phone || detailTarget.member?.phone || '-'}
                  </dd>
                </div>
              </dl>
            )}

            {/* 현장별 등록된 역할 (SiteContact 참조, 읽기전용 — SiteContactsSection 패턴 반대 방향)
                구성원(countsAsMember)은 내부 멤버로 SiteContact 개념 미해당 → 섹션 숨김(옛 role OWNER/SAFETY_MANAGER 조건) */}
            {!isEditingInfo && !isOrganizationMember(detailTarget) && (
              <div className="mt-3">
                <p className="text-xs font-medium text-ink-500 mb-2">등록된 역할</p>
                {(detailTarget.siteContacts?.length ?? 0) === 0 ? (
                  <p className="text-sm text-ink-400 py-2">등록된 역할이 없습니다.</p>
                ) : (
                  <ul className="divide-y divide-line-soft">
                    {detailTarget.siteContacts!.map((sc) => {
                      const dept = sc.department || '';
                      const pos = sc.position || '';
                      const roleLabel = [dept, pos].filter(Boolean).join(' / ') || '(역할 미지정)';
                      return (
                        <li key={sc.siteId} className="py-2">
                          <p className="text-xs text-ink-400 mb-0.5">{sc.siteName}</p>
                          <span className="text-sm text-ink-900">{roleLabel}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* (옛 「권한 설정」 토글 — canManageSites/Inspections boolean — 은 P7-a에서 서버 컬럼과 함께 제거.
              멤버별 권한은 역할·선임 탭의 역할 배정으로 편집한다) */}

          {/* 담당 현장 목록 */}
          <div className="border-t border-line-soft pt-4">
            <h4 className={`font-medium text-ink-900 mb-3 ${isMobile ? 'text-sm' : 'text-base'}`}>
              담당 현장 ({assignedSites.length}개)
            </h4>

            {isLoadingSites ? (
              <p className="text-sm text-ink-400 py-2">현장 정보를 불러오는 중...</p>
            ) : assignedSites.length === 0 ? (
              <p className={`text-ink-400 py-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>할당된 현장이 없습니다.</p>
            ) : (
              <ul className="divide-y divide-line-soft mb-3 max-h-48 overflow-y-auto">
                {assignedSites.map(a => (
                  <li key={a.id} className="flex items-center justify-between py-2">
                    <div className="flex items-center min-w-0">
                      <BuildingIcon className={`text-ink-400 flex-shrink-0 ${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
                      <div className="ml-2 min-w-0">
                        <span className={`text-ink-900 block truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {a.siteName}
                        </span>
                        {a.siteAddress && (
                          <span className={`text-ink-400 block truncate ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                            {a.siteAddress}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* 배정 해제는 소유자 **성질** — 서버 `sites.service.unassignMember`가 `site.assign`
                        가드를 통과한 뒤에도 `isOwnerMember`를 또 본다. 키로 바꾸면 눌리는데 403이 난다. */}
                    {data.isOwner && (
                      <TextButton
                        tone="danger"
                        onClick={() => handlers.handleUnassignSite(a.siteId)}
                        disabled={isUnassigning === a.siteId}
                        className={`ml-2 flex-shrink-0 hover:bg-red-50 px-2 py-0.5 ${isMobile ? 'text-xs' : 'text-sm'}`}
                      >
                        {isUnassigning === a.siteId ? '...' : '해제'}
                      </TextButton>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* 현장 추가 콤보박스 — 배정도 소유자 **성질**이다(서버 `assignMember`가 `isOwnerMember`를 본다).
                목록 자체는 읽기 정보라 그대로 보여 준다: 가릴 것은 «바꾸는 손»뿐이다. */}
            {data.isOwner && (
            <div ref={dropdownRef} className="relative mt-3">
              <label className={`block font-medium text-ink-700 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                현장 추가
              </label>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="현장명 또는 주소로 검색..."
                className={`w-full px-3 py-2 border border-line rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 text-sm`}
              />
              {isDropdownOpen && (
                <ul ref={dropdownListRef} className="absolute z-10 mt-1 w-full bg-white border border-line-soft rounded-card shadow-card max-h-48 overflow-y-auto">
                  {isLoadingSites ? (
                    <li className="px-3 py-2 text-sm text-ink-400">불러오는 중...</li>
                  ) : filteredSites.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-ink-400">
                      {searchQuery.trim() ? '검색 결과가 없습니다.' : '추가할 수 있는 현장이 없습니다.'}
                    </li>
                  ) : (
                    filteredSites.map(site => (
                      <li
                        key={site.id}
                        onClick={() => {
                          handlers.handleAssignSite(site);
                          setIsDropdownOpen(false);
                          setSearchQuery('');
                        }}
                        className="px-3 py-2 hover:bg-brand-50 cursor-pointer"
                      >
                        <span className={`block text-ink-900 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {site.name}
                        </span>
                        {site.address && (
                          <span className={`block text-ink-400 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                            {site.address}
                          </span>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            )}
          </div>

    </Modal>

      {/* 아래 셋은 상세 모달 **위**의 sub 층이다(층은 여는 쪽이 정한다 — `modalLayers.ts`).
          상세 모달의 children이 아니라 **형제**로 둔다: children이면 React 트리상 상세 패널의
          클릭·키 이벤트 핸들러(포커스 트랩 포함)를 거쳐 버블링된다. Esc는 base 레지스트리가
          최상단(sub) 하나만 닫는다. 폭은 base 단계에 맞춰 `sm` — 종전 모바일 `max-w-xs`는 base에 없다. */}

      {/* 할당 확인 모달 */}
      <Modal
        open={!!assignConfirmSite}
        onClose={handlers.handleCancelAssign}
        title="현장 할당"
        size="sm"
        overlayZClassName={MODAL_LAYER.sub}
        overlayBgClassName={SUB_MODAL_OVERLAY_BG}
        closeOnOverlay={!isAssigning}
        closeOnEsc={!isAssigning}
        showCloseButton={!isAssigning}
        closeLocked={isAssigning}
        footer={
          <>
            <Button size="sm" onClick={handlers.handleCancelAssign} disabled={isAssigning}>
              취소
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handlers.handleConfirmAssign}
              disabled={isAssigning}
            >
              {isAssigning ? '할당 중...' : '할당'}
            </Button>
          </>
        }
      >
        {assignConfirmSite && (
          <p className={`text-ink-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            <strong>{getMemberDisplayName(detailTarget)}</strong>님을{' '}
            <strong>{assignConfirmSite.name}</strong> 현장에 할당하시겠습니까?
          </p>
        )}
      </Modal>

      {/* 해제 확인 모달 */}
      <Modal
        open={!!unassignConfirmSiteId}
        onClose={handlers.handleCancelUnassign}
        title="현장 할당 해제"
        size="sm"
        overlayZClassName={MODAL_LAYER.sub}
        overlayBgClassName={SUB_MODAL_OVERLAY_BG}
        closeOnOverlay={!isUnassigning}
        closeOnEsc={!isUnassigning}
        showCloseButton={!isUnassigning}
        closeLocked={!!isUnassigning}
        footer={
          <>
            <Button size="sm" onClick={handlers.handleCancelUnassign} disabled={!!isUnassigning}>
              취소
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handlers.handleConfirmUnassign}
              disabled={!!isUnassigning}
            >
              {isUnassigning ? '해제 중...' : '해제'}
            </Button>
          </>
        }
      >
        <p className={`text-ink-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <strong>{getMemberDisplayName(detailTarget)}</strong>님의{' '}
          <strong>{unassignSiteName}</strong> 현장 할당을 해제하시겠습니까?
        </p>
      </Modal>

      {/* 이관 모달 (진행중 점검이 있는 경우) */}
      {transferRequired && (
        <TransferModal
          detailTarget={detailTarget}
          transferRequired={transferRequired}
          transferTargetMemberId={transferTargetMemberId}
          isTransferring={isTransferring}
          members={data.members}
          handlers={handlers}
          isMobile={isMobile}
        />
      )}
    </>
  );
}

function TransferModal({
  detailTarget,
  transferRequired,
  transferTargetMemberId,
  isTransferring,
  members,
  handlers,
  isMobile,
}: {
  detailTarget: OrganizationMember;
  transferRequired: { siteId: string; siteName: string; activeInspectionCount: number };
  transferTargetMemberId: string | null;
  isTransferring: boolean;
  members: OrganizationMember[];
  handlers: MembersHandlers;
  isMobile?: boolean;
}) {
  // 이관 가능한 멤버: 해당 현장에 할당된 다른 멤버 우선, 나머지 멤버도 포함
  const transferCandidates = useMemo(() => {
    const assignedToSite = new Set<string>();
    for (const m of members) {
      if (m.id !== detailTarget.id && m.siteAssignments?.some(a => a.siteId === transferRequired.siteId)) {
        assignedToSite.add(m.id);
      }
    }
    const others = members.filter(m => m.id !== detailTarget.id);
    // 해당 현장 할당 멤버를 먼저, 나머지를 뒤에
    return others.sort((a, b) => {
      const aAssigned = assignedToSite.has(a.id) ? 0 : 1;
      const bAssigned = assignedToSite.has(b.id) ? 0 : 1;
      return aAssigned - bAssigned;
    }).map(m => ({
      ...m,
      isAssignedToSite: assignedToSite.has(m.id),
    }));
  }, [members, detailTarget.id, transferRequired.siteId]);

  // 상세 모달 위의 sub 층. 처리 중에는 닫는 길 전부를 막는다(종전 오버레이 클릭의
  // `!isTransferring` 가드와 같은 축). 버튼 두 줄(취소·이관 후 해제 / 강제 해제)은 종전 배치가
  // 세로 2단이라 base 푸터(가로 한 줄)로 옮기지 않고 본문에 둔다.
  return (
    <Modal
      open
      onClose={handlers.handleCancelTransfer}
      title="진행중인 점검이 있습니다"
      size={isMobile ? 'sm' : 'md'}
      overlayZClassName={MODAL_LAYER.sub}
      overlayBgClassName={SUB_MODAL_OVERLAY_BG}
      closeOnOverlay={!isTransferring}
      closeOnEsc={!isTransferring}
      showCloseButton={!isTransferring}
      closeLocked={isTransferring}
    >
      {/* 경고 안내 */}
      <div className="flex items-start mb-4">
        <div className={`flex-shrink-0 flex items-center justify-center rounded-full bg-yellow-100 ${isMobile ? 'h-9 w-9' : 'h-10 w-10'}`}>
          <ExclamationIcon className={`text-yellow-600 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
        </div>
        <p className={`ml-3 text-ink-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <strong>{getMemberDisplayName(detailTarget)}</strong>님은{' '}
          <strong>{transferRequired.siteName}</strong> 현장에서 진행중인 점검이{' '}
          <strong>{transferRequired.activeInspectionCount}건</strong> 있습니다.
        </p>
      </div>

      {/* 이관 대상 선택 */}
      <div className="mb-4">
        <label className={`block font-medium text-ink-700 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          점검 이관 대상
        </label>
        <select
          value={transferTargetMemberId || ''}
          onChange={(e) => handlers.setTransferTargetMemberId(e.target.value || null)}
          className={`w-full px-3 py-2 border border-line rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 text-sm`}
        >
          <option value="">선택해주세요</option>
          {transferCandidates.map(m => (
            <option key={m.id} value={m.id}>
              {getMemberDisplayName(m)}
              {m.isAssignedToSite ? ' (현장 담당자)' : ''}
            </option>
          ))}
        </select>
        <p className={`mt-1 text-ink-400 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
          진행중인 점검의 담당자가 선택한 멤버로 변경됩니다.
        </p>
      </div>

      {/* 버튼들 */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-end space-x-2">
          <Button size="sm" onClick={handlers.handleCancelTransfer} disabled={isTransferring}>
            취소
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handlers.handleTransferAndUnassign}
            disabled={isTransferring || !transferTargetMemberId}
          >
            {isTransferring ? '처리 중...' : '이관 후 해제'}
          </Button>
        </div>
        <div className="border-t border-line-soft pt-2">
          <button
            onClick={handlers.handleForceUnassign}
            disabled={isTransferring}
            className={`w-full px-3 py-1.5 font-medium text-red-600 bg-white border border-red-200 rounded-card-sm hover:bg-red-50 disabled:opacity-50 ${isMobile ? 'text-xs' : 'text-sm'}`}
          >
            {isTransferring ? '처리 중...' : '강제 해제 (본인에게 이관)'}
          </button>
          <p className={`mt-1 text-center text-ink-400 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
            퇴사 등 긴급한 경우에 사용하세요. 진행중 점검이 나에게 이관됩니다.
          </p>
        </div>
      </div>
    </Modal>
  );
}
